import React from "react";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import toast from "react-hot-toast";

import {
  useGetBenefitFeaturedEventsQuery,
  useReorderBenefitFeaturedEventsMutation,
  useToggleBenefitFeaturedEventActiveMutation,
  useDeleteBenefitFeaturedEventMutation,
} from "../../store/services/benefits";
import { BenefitFeaturedEvent } from "../../models/benefits";
import { AppDialog, FormSection, RowActions, StatusBadge, rowActionsTableStyle } from "../../components/ui";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";
import { AddFeaturedEventDialog } from "./AddFeaturedEventDialog";

interface BenefitFeaturedEventsSectionProps {
  /** Cantidad máxima de eventos destacados activos, según la configuración guardada. */
  maxCount: number;
}

export const BenefitFeaturedEventsSection = ({
  maxCount,
}: BenefitFeaturedEventsSectionProps) => {
  const { data, isLoading, isFetching, refetch } = useGetBenefitFeaturedEventsQuery();
  const [reorder] = useReorderBenefitFeaturedEventsMutation();
  const [toggleActive] = useToggleBenefitFeaturedEventActiveMutation();
  const [deleteFeaturedEvent, { isLoading: isDeleting }] =
    useDeleteBenefitFeaturedEventMutation();

  const items = React.useMemo(() => data?.items ?? [], [data]);

  const sortedItems = React.useMemo(
    () => [...items].sort((a, b) => a.sortOrder - b.sortOrder),
    [items]
  );

  // Copia local para feedback instantáneo del drag & drop; se resincroniza con
  // el server salvo mientras se está persistiendo un reordenamiento.
  const [isSavingOrder, setIsSavingOrder] = React.useState(false);
  const [rows, setRows] = React.useState<BenefitFeaturedEvent[]>([]);
  const [itemToDelete, setItemToDelete] = React.useState<BenefitFeaturedEvent>();
  const [isAddDialogVisible, setIsAddDialogVisible] = React.useState(false);

  React.useEffect(() => {
    if (!isSavingOrder) setRows(sortedItems);
  }, [sortedItems, isSavingOrder]);

  const activeCount = items.filter((item) => item.active).length;
  const canAddMore = activeCount < maxCount;

  const hideDeleteDialog = () => setItemToDelete(undefined);

  const handleRowReorder = (e: DataTableRowReorderEvent<BenefitFeaturedEvent[]>) => {
    setRows(e.value);
    setIsSavingOrder(true);
    reorder(e.value.map((item) => item.id))
      .unwrap()
      .then(() => toast.success("Orden actualizado"))
      .catch((err) => {
        toast.error(getAdminFacingApiErrorMessage(err, "No se pudo actualizar el orden"));
        void refetch();
      })
      .finally(() => setIsSavingOrder(false));
  };

  const handleToggleActive = (item: BenefitFeaturedEvent, active: boolean) => {
    toggleActive({ id: item.id, active })
      .unwrap()
      .catch((err) => {
        toast.error(getAdminFacingApiErrorMessage(err, "No se pudo actualizar el evento"));
      });
  };

  const handleDelete = (item: BenefitFeaturedEvent) => {
    deleteFeaturedEvent(item.id)
      .unwrap()
      .then(() => toast.success("Evento quitado de destacados"))
      .catch((err) => {
        toast.error(getAdminFacingApiErrorMessage(err, "No se pudo quitar el evento"));
      })
      .finally(hideDeleteDialog);
  };

  const nameBodyTemplate = (item: BenefitFeaturedEvent) => (
    <span>
      {item.eventName ?? "-"}{" "}
      <span className="text-color-secondary text-sm">#{item.eventId}</span>
    </span>
  );

  const dateBodyTemplate = (item: BenefitFeaturedEvent) =>
    item.eventStartDate
      ? new Date(item.eventStartDate).toLocaleDateString("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "-";

  const finishedBodyTemplate = (item: BenefitFeaturedEvent) =>
    item.isPast ? (
      <StatusBadge label="Sí" tone="error" />
    ) : (
      <StatusBadge label="No" tone="success" />
    );

  const activeBodyTemplate = (item: BenefitFeaturedEvent) => (
    <InputSwitch
      checked={item.active}
      onChange={(e) => handleToggleActive(item, Boolean(e.value))}
      disabled={!item.active && !canAddMore}
    />
  );

  const actionsBodyTemplate = (item: BenefitFeaturedEvent) => (
    <RowActions
      actions={[
        { icon: "pi pi-trash", label: "Quitar", severity: "danger", onClick: () => setItemToDelete(item) },
      ]}
    />
  );

  return (
    <FormSection
      title="Eventos destacados"
      defaultOpen
      titleAction={
        // §6.1 — el alta es un `+` circular; el motivo del bloqueo va en el tooltip.
        <Button
          type="button"
          icon="pi pi-plus"
          rounded
          size="small"
          aria-label="Agregar evento"
          onClick={() => setIsAddDialogVisible(true)}
          disabled={!canAddMore}
          tooltip={
            !canAddMore
              ? `Alcanzaste el máximo de ${maxCount} eventos destacados activos`
              : "Agregar evento"
          }
          tooltipOptions={{ position: "top", showOnDisabled: true }}
        />
      }
    >

      <DataTable
        style={rowActionsTableStyle("4.5rem", "4.5rem")}
        value={rows}
        dataKey="id"
        stripedRows
        className="equal-columns-table"
        loading={isLoading || isFetching || isDeleting || isSavingOrder}
        emptyMessage="No hay eventos destacados todavía"
        reorderableRows={!isSavingOrder}
        onRowReorder={handleRowReorder}
      >
        <Column rowReorder style={{ width: "3.5rem", minWidth: "3.5rem" }} />
        <Column header="Evento" style={{ width: "33.333%" }} body={nameBodyTemplate} />
        <Column header="Fecha" style={{ width: "16.667%" }} body={dateBodyTemplate} />
        <Column header="Finalizado" style={{ width: "16.667%" }} body={finishedBodyTemplate} alignHeader="center" bodyClassName="text-center" />
        <Column header="Activo" style={{ width: "16.667%" }} body={activeBodyTemplate} alignHeader="center" bodyClassName="text-center" />
        <Column
          header={<span className="row-actions-label">Acciones</span>}
          // Bloque contra el borde derecho con el mismo margen que la primera columna
          // a la izquierda; título e íconos centrados dentro del bloque.
          style={{ width: "16.667%" }}
          headerClassName="row-actions-th"
          bodyClassName="row-actions-cell"
          body={actionsBodyTemplate}
          exportable={false}
        />
      </DataTable>

      <AppDialog
        variant="destructive"
        visible={!!itemToDelete}
        onHide={hideDeleteDialog}
        header="Quitar evento destacado"
        primaryLabel="Quitar Evento"
        onPrimary={() => itemToDelete && handleDelete(itemToDelete)}
        loading={isDeleting}
      >
        <span>
          ¿Estás seguro de que querés quitar{" "}
          <strong>{itemToDelete?.eventName}</strong> de los eventos destacados?
        </span>
      </AppDialog>

      <AddFeaturedEventDialog
        visible={isAddDialogVisible}
        onHide={() => setIsAddDialogVisible(false)}
        existingEventIds={items.map((item) => item.eventId)}
      />
    </FormSection>
  );
};
